// AI Config → Usage & limits: estimated spend per AI feature, recent calls, monthly £ caps.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gauge, Loader2, RefreshCw, PauseCircle } from "lucide-react";
import api, { formatApiError } from "../../lib/api";

const gbp = (n) => `£${Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: n < 1 ? 4 : 2 })}`;
const when = (ts) => new Date(ts).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

function CapInput({ f, onSaved }) {
  const [v, setV] = useState(f.cap_gbp ?? "");
  const save = async () => {
    try { await api.put(`/ai-usage/caps/${f.key}`, { monthly_gbp: v === "" ? 0 : v }); toast.success(v ? `Cap set: ${gbp(v)}/month` : "Cap removed"); onSaved(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  return (
    <div className="flex items-center gap-1">
      <span className="text-slate-500 text-[11px]">£</span>
      <input type="number" min="0" step="1" value={v} onChange={(e) => setV(e.target.value)} onBlur={() => String(v) !== String(f.cap_gbp ?? "") && save()}
        placeholder="no cap" className="w-20 rounded-md border border-white/10 bg-white/[0.03] px-2 py-1 text-[12px] text-white" data-testid={`ai-cap-${f.key}`} />
    </div>
  );
}

export default function AIUsagePanel() {
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  const [calls, setCalls] = useState([]);
  const [feat, setFeat] = useState("");
  const load = () => {
    api.get(`/ai-usage/summary?days=${days}`).then(({ data }) => setD(data)).catch(() => {});
    api.get(`/ai-usage/calls?limit=60${feat ? `&feature=${feat}` : ""}`).then(({ data }) => setCalls(data.items || [])).catch(() => {});
  };
  useEffect(load, [days, feat]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return <div className="py-6 grid place-items-center"><Loader2 className="w-4 h-4 animate-spin text-slate-500" /></div>;
  const maxDay = Math.max(...d.daily.map((x) => x.cost_gbp), 0.0001);
  const byDay = Object.fromEntries(d.daily.map((x) => [x.day, x]));
  const series = Array.from({ length: days }, (_, i) => {
    const day = new Date(Date.now() - (days - 1 - i) * 86400000).toISOString().slice(0, 10);
    return byDay[day] || { day, cost_gbp: 0, calls: 0 };
  });
  const used = d.features.filter((f) => f.calls || f.cap_gbp);
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-4" data-testid="ai-usage-panel">
      <div className="flex items-center gap-3 flex-wrap">
        <Gauge className="w-5 h-5 text-amber-300" />
        <div className="flex-1 min-w-[200px]">
          <div className="text-white font-semibold">AI usage & limits</div>
          <div className="text-[12px] text-slate-400">{d.note}</div>
        </div>
        <div className="flex gap-1">
          {[7, 30, 90].map((n) => <button key={n} type="button" onClick={() => setDays(n)} data-testid={`ai-usage-days-${n}`}
            className={`px-2.5 py-1 rounded-md text-[12px] border ${days === n ? "border-amber-400/60 bg-amber-500/15 text-amber-200" : "border-white/10 text-slate-400"}`}>{n}d</button>)}
          <button type="button" onClick={load} className="px-2 py-1 rounded-md border border-white/10 text-slate-400 hover:text-white" title="Refresh"><RefreshCw className="w-3.5 h-3.5" /></button>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {[["This month", gbp(d.month_gbp), "ai-usage-month"], [`Last ${days} days`, gbp(d.total_gbp), "ai-usage-total"], ["AI calls", d.calls.toLocaleString(), "ai-usage-calls"]].map(([l, v, t]) => (
          <div key={l} className="rounded-xl border border-white/[0.06] p-3"><div className="text-xl font-bold text-white tabular-nums" data-testid={t}>{v}</div><div className="text-[10.5px] uppercase tracking-widest text-slate-500 font-mono">{l}</div></div>
        ))}
      </div>
      {d.daily.length > 0 && (
        <div className="flex items-end gap-[3px] h-16" data-testid="ai-usage-chart" title="Estimated £ per day">
          {series.map((x) => <div key={x.day} className={`flex-1 rounded-t ${x.cost_gbp ? "bg-amber-400/70" : "bg-white/[0.06]"}`} style={{ height: `${x.cost_gbp ? Math.max(6, (x.cost_gbp / maxDay) * 100) : 3}%` }} title={`${x.day}: ${gbp(x.cost_gbp)} · ${x.calls} calls`} />)}
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]" data-testid="ai-usage-table">
          <thead><tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 font-mono">
            <th className="py-1.5 pr-2">Feature</th><th className="pr-2 text-right">Calls</th><th className="pr-2 text-right">Est. £ ({days}d)</th><th className="pr-2 text-right">This month</th><th className="pr-2">Monthly cap</th><th>Models</th></tr></thead>
          <tbody>
            {used.length === 0 && <tr><td colSpan={6} className="py-3 text-slate-500">No AI calls recorded yet — tracking started with this update.</td></tr>}
            {used.map((f) => {
              const capped = f.cap_gbp && f.month_gbp >= f.cap_gbp;
              return (
                <tr key={f.key} className="border-t border-white/[0.05]" data-testid={`ai-usage-row-${f.key}`}>
                  <td className="py-2 pr-2"><button type="button" onClick={() => setFeat(feat === f.key ? "" : f.key)} className={`text-left ${feat === f.key ? "text-amber-200" : "text-slate-100"} hover:underline`}>{f.label}</button>
                    {capped && <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-rose-300"><PauseCircle className="w-3 h-3" />paused</span>}
                    {f.fails > 0 && <span className="ml-2 text-[10px] text-slate-500">{f.fails} failed</span>}</td>
                  <td className="pr-2 text-right tabular-nums text-slate-300">{f.calls}</td>
                  <td className="pr-2 text-right tabular-nums text-white">{gbp(f.cost_gbp)}</td>
                  <td className="pr-2 text-right tabular-nums text-slate-300">{gbp(f.month_gbp)}</td>
                  <td className="pr-2"><CapInput f={f} onSaved={load} /></td>
                  <td className="text-[11px] text-slate-500 font-mono">{f.models.join(", ")}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {d.people.length > 0 && <div className="text-[12px] text-slate-400" data-testid="ai-usage-people">By person: {d.people.map((p) => `${p.user} ${gbp(p.cost_gbp)} (${p.calls})`).join(" · ")}</div>}
      <details className="rounded-lg border border-white/[0.06]" data-testid="ai-usage-history">
        <summary className="px-3 py-2 cursor-pointer text-[12.5px] text-slate-300">Recent calls{feat ? ` · ${used.find((x) => x.key === feat)?.label || feat}` : ""} ({calls.length})</summary>
        <div className="max-h-72 overflow-y-auto divide-y divide-white/[0.04]">
          {calls.map((c, i) => (
            <div key={i} className="px-3 py-1.5 grid grid-cols-[110px_1fr_auto] gap-2 text-[11.5px]">
              <span className="text-slate-500 font-mono">{when(c.ts)}</span>
              <span className="text-slate-300 truncate">{c.label} · {c.user} · <span className="text-slate-500 font-mono">{c.model}</span>{!c.ok && <span className="text-rose-300"> · failed</span>}</span>
              <span className="text-slate-200 tabular-nums">{gbp(c.cost_gbp)}<span className="text-slate-500"> · {((c.in_tokens || 0) + (c.out_tokens || 0)).toLocaleString()} tok</span></span>
            </div>
          ))}
        </div>
      </details>
    </div>
  );
}
